import {
  AsyncThunkPayloadCreator,
  combineReducers,
  configureStore,
  createAsyncThunk,
  Reducer,
} from "@reduxjs/toolkit"
import accessoriesReducer from "components/shopFeatures/accessories/accessoriesSlice"
import authReducer from "components/shopFeatures/auth/authSlice"
import colorReducer from "components/shopFeatures/color/colorSlice"
import detailingReducer from "components/shopFeatures/detailing/detailingSlice"
import paymentReducer from "components/shopFeatures/payment/paymentSlice"
import showRoomsReducer from "components/shopFeatures/showRooms/showRoomsSlice"
import upholsteryReducer from "components/shopFeatures/upholstery/upholsterySlice"
import wizardReducer from "components/shopFeatures/wizard/wizardSlice"
import collapseLayout from "layout/oldLayout/CollapseLayout"
import layoutReducer from "layout/oldLayout/layoutSlice"
import { HYDRATE } from "next-redux-wrapper"
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux"

// there is a problem with reducers:
const combinedReducer = combineReducers({
  auth: authReducer,
  layout: layoutReducer,
  showRooms: showRoomsReducer,
  wizard: wizardReducer,
  detailing: detailingReducer,
  color: colorReducer,
  upholstery: upholsteryReducer,
  accessories: accessoriesReducer,
  payment: paymentReducer,
  collapseLayout: collapseLayout,
})

export type RootState = ReturnType<typeof combinedReducer>

const reducer: Reducer<RootState> = (state, action) => {
  if (action.type === HYDRATE) {
    const nextState = {
      ...state, // use previous state
      ...(action.payload as RootState), // apply delta from hydration
    }

    // Preserve layout, auth, and showRooms states
    if (state) {
      nextState.layout = state.layout
      nextState.auth = state.auth
      nextState.showRooms = state.showRooms
    }

    return nextState
  }

  return combinedReducer(state, action)
}

export const makeStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Ignore these action types
          ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
          // Ignore these field paths in all actions
          ignoredActionsPaths: ["meta.arg", "payload.timestamp"],
          // Ignore these paths in the state
          ignoredPaths: ["items.dates"],
        },
      }),
  })
}

export type AppStore = ReturnType<typeof makeStore>
export type AppState = ReturnType<AppStore["getState"]>

export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector

export type AppDispatch = ReturnType<typeof makeStore>["dispatch"]
export const useAppDispatch = () => useDispatch<AppDispatch>()

export type ThunkApiConfig = {
  state: RootState
  rejectValue: { code: number; message: string }
}
export function createAsyncAppThunk<Returned, ThunkArg = void>(
  typePrefix: string,
  payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, ThunkApiConfig>
) {
  return createAsyncThunk<Returned, ThunkArg, ThunkApiConfig>(
    typePrefix,
    payloadCreator
  )
}
